// Spec 47 approval-ux — cmp_approval_sla_pill / sla_urgency_indicator.
//
// "Pill indicating SLA status: on_time (neutral), due_soon (state_warning),
// overdue (state_danger). Always paired with icon + text — never color
// alone. Used on inbox rows and inside decision cards." (spec 47 line 716)
//
// Spec 47 a11y:
//   - Pill uses aria-label='SLA: {{state}} — {{delta}}'
//   - Color roles: on_time=color_neutral_500 bg color_neutral_100;
//     due_soon=color_warning_600 on color_warning_50; overdue=color_danger_
//     600 on color_danger_50 — applied via data-state attribute (CSS owns
//     actual values).
//   - Icon carries semantic weight duplicated in text.

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export type SLAState = "on_time" | "due_soon" | "overdue";

export interface SLAPillProps extends HTMLAttributes<HTMLSpanElement> {
  readonly state: SLAState;
  /** Visible label, e.g., "On time" / "Due in 6h" / "Overdue by 2h". */
  readonly label: string;
  /** Optional secondary text (e.g., "before 5:00 PM"). Folded into aria-label. */
  readonly delta?: string;
  /** Decorative icon (renders aria-hidden). Caller supplies clock / clock-countdown / warning-octagon. */
  readonly icon?: ReactNode;
}

const STATE_LABEL: Readonly<Record<SLAState, string>> = {
  on_time: "On time",
  due_soon: "Due soon",
  overdue: "Overdue",
};

export const SLAPill = forwardRef<HTMLSpanElement, SLAPillProps>(
  ({ state, label, delta, icon, className, ...rest }, ref) => {
    // Spec a11y: aria-label='SLA: {{state}} — {{delta}}'. State is the
    // canonical phrase ("On time" / "Due soon" / "Overdue"); label is the
    // formatted human text ("Due in 6h"). Combine for screen reader
    // clarity without duplicating "soon" / "by 2h" awkwardly.
    const ariaLabelParts: string[] = [`SLA: ${STATE_LABEL[state]}`, label];
    if (delta !== undefined) ariaLabelParts.push(delta);
    const ariaLabel = ariaLabelParts.join(" — ");

    return (
      <span
        ref={ref}
        data-component="sla-pill"
        data-state={state}
        aria-label={ariaLabel}
        className={cx("nai-sla-pill", `nai-sla-pill--${state}`, className)}
        {...rest}
      >
        {icon !== undefined && (
          <span aria-hidden="true" data-component="sla-pill-icon" className="nai-sla-pill-icon">
            {icon}
          </span>
        )}
        <span data-component="sla-pill-label" className="nai-sla-pill-label">
          {label}
        </span>
        {delta !== undefined && (
          <span data-component="sla-pill-delta" className="nai-sla-pill-delta">
            {delta}
          </span>
        )}
      </span>
    );
  },
);
SLAPill.displayName = "SLAPill";
